import React, { useCallback, useEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';
import { useScrollspy } from '@sg/garnish';
import {
  type CustomizationMachineActorRef,
  useCustomizationHelpers,
} from '@sg/shared-fe/features/customization';
import { useCustomizationContext } from '@sg/shared-fe/features/customization';

import { CustomizationIngredientsGrid } from '@order/features/customization';
import { useTelemetry } from '@order/Telemetry';

import { useStepperTimeout } from '../../../../../ProductDetailsContentV2/components/ProductDetailsIngredientsView/hooks';
import { useModificationGroupHelperText } from '../../../../hooks';
import { CustomizationModificationGroup } from '../CustomizationModificationGroup';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsGridPanel = (props: Props) => {
  const { actor, activeTargetId } = props;

  const { track } = useTelemetry();

  const getSectionHelperText = useModificationGroupHelperText();
  const { activeStepperId, setActiveStepperId, resetStepperTimeout } = useStepperTimeout(); // prettier-ignore

  const scrollSpy = useScrollspy({
    defaultActiveTargetId: activeTargetId ?? undefined,
  });
  const { helpers, setActiveTargetId } = scrollSpy;

  const { modificationGroups, modificationLimits } = useCustomizationContext({
    actor,
  });

  const { getFormattedQuantity } = useCustomizationHelpers({ actor });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onAddIngredient = useCallback(() => {
    track('customization.ingredients-tab.ingredient-added');
  }, [track]);

  const onRemoveIngredient = useCallback(() => {
    track('customization.ingredients-tab.ingredient-removed');
  }, [track]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!activeTargetId) return;

    setActiveTargetId(activeTargetId);
  }, [activeTargetId, setActiveTargetId]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SafeAreaView
      edges={SAFE_AREA_EDGES}
      style={styles.ingredientsGridContainer}
    >
      <CustomizationIngredientsGrid.Container
        register={helpers.scrollView.register}
        deregister={helpers.scrollView.deregister}
        storeContentSize={helpers.scrollView.storeContentSize}
        storeSize={helpers.scrollView.storeSize}
        trackScroll={helpers.scrollView.trackScroll}
      >
        {modificationGroups?.map((group) => {
          const freeAggregateQuantity =
            modificationLimits[group.name]?.freeAggregateQuantity;

          return (
            <CustomizationModificationGroup
              key={group.id}
              actor={actor}
              onAddIngredient={onAddIngredient}
              onRemoveIngredient={onRemoveIngredient}
              helperText={getSectionHelperText({
                freeAggregateQuantity,
                ingredientKind: group.modificationIngredientKind,
              })}
              formattedQuantity={getFormattedQuantity(group.name)}
              modificationGroup={group}
              activeStepperId={activeStepperId}
              resetStepperTimeout={resetStepperTimeout}
              setActiveStepperId={setActiveStepperId}
              scrollSpy={scrollSpy}
            />
          );
        })}
      </CustomizationIngredientsGrid.Container>
    </SafeAreaView>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['bottom'],
  default: [],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ingredientsGridContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: CustomizationMachineActorRef;
  activeTargetId: string | null | undefined;
};
